import React, { ReactElement, useRef, useCallback, useEffect, useState, FC } from 'react'

import { DrawerProps } from './types'
import './index.css'

const Drawer: FC<DrawerProps> = (props): ReactElement => {
  const [openDrawer, setOpenDrawer] = useState(false)

  const DRAWER_ANIMATION_DURATION = 300
  const { t, children, onClose, anchor } = props
  const drawerBGRef = useRef<HTMLDivElement>(null)
  const drawerLayoutRef = useRef<HTMLDivElement>(null)

  const drawerBG = drawerBGRef.current as HTMLElement
  const drawerLayout = drawerLayoutRef.current as HTMLElement

  const closeDrawer = useCallback((): void => {
    if (openDrawer) {
      drawerBG.classList.add('slide-out-bg')
      drawerLayout.classList.add(`slide-out-${anchor}`)
      setTimeout(() => {
        // for the Drawer component to close just before the animation is complete
        setOpenDrawer(false)
        drawerLayoutRef.current?.blur()
        const focusElement = document.querySelector('#focus-element') as HTMLElement
        focusElement.focus()
        onClose()
      }, DRAWER_ANIMATION_DURATION - 20) // close the drawer just before the animation completes
    }
  }, [onClose, openDrawer, drawerBG, drawerLayout, anchor])

  const escKeyPress = useCallback(
    (e): void => {
      if (e.key === `Escape`) {
        closeDrawer()
      }
    },
    [closeDrawer]
  )

  const getFocusableElements = useCallback((): NodeListOf<Element> | undefined => {
    return drawerLayoutRef.current?.querySelectorAll(
      'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
    )
  }, [drawerLayoutRef])

  useEffect(() => {
    setTimeout(() => {
      setOpenDrawer(true)
      const focusableElements = getFocusableElements()
      if (focusableElements?.length) (focusableElements[0] as HTMLElement).focus()
    }, 350) // Extra 50ms to let the animation complete before closing the Drawer component
    return
  }, [getFocusableElements])

  useEffect(() => {
    document.addEventListener('keydown', escKeyPress)
    return () => document.removeEventListener('keydown', escKeyPress)
  }, [escKeyPress])

  const childrenWithCloseProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      const newProps = { onClick: closeDrawer }
      return React.cloneElement(child, newProps)
    }
    return child
  })

  return (
    <div role="dialog">
      <div className="drawer-background" ref={drawerBGRef} onClick={closeDrawer} onKeyPress={escKeyPress} role="presentation"></div>
      <div className={`drawer-layout drawer-${anchor}`} ref={drawerLayoutRef}>
        <button className="drawer-hidden-close-button" onClick={closeDrawer} aria-label={t('Common_CloseDrawer')}></button>
        <div className="drawer-elements">{childrenWithCloseProps}</div>
      </div>
    </div>
  )
}

export default Drawer
